import { ComponentPropsWithoutRef, useCallback, useContext, useMemo } from 'react';
import { Table, TableState } from '@tanstack/react-table';
import { Button } from '@givebutter/primitives/button';
import { TableContext } from 'components/table/TableContext';
import { isEqual, isEqualWith } from 'lodash';
import { twMerge } from 'tailwind-merge';

interface TableResetProps extends Omit<ComponentPropsWithoutRef<'button'>, 'children' | 'color'> {}

const TableReset = (props: TableResetProps) => {
    const { table } = useContext(TableContext);
    const { type = 'button', className = '', onClick, ...rest } = props;
    const { reset, canReset } = useTableReset(table);

    if (!canReset) {
        return null;
    }

    return (
        <Button
            variant="none"
            color="primary"
            size="sm"
            onClick={e => {
                reset();
                onClick?.(e);
            }}
            className={twMerge('px-1 font-medium', className)}
            {...rest}
        >
            Reset defaults
        </Button>
    );
};

const useTableReset = <T,>(table: Table<T>) => {
    const tableState = table.getState();
    const canReset = useMemo(
        () =>
            !isEqualWith(
                table.initialState,
                tableState,
                (tableX: TableState, tableY: TableState) => {
                    const {
                        pagination: xPagination,
                        rowSelection: _xRowSelection,
                        isAllRecordsSelected: _xIsAllRecordsSelected,
                        ...restTableX
                    } = tableX;
                    const {
                        pagination: yPagination,
                        rowSelection: _yRowSelection,
                        isAllRecordsSelected: _yIsAllRecordsSelected,
                        ...restTableY
                    } = tableY;

                    return isEqual(
                        { pageSize: xPagination.pageSize, ...restTableX },
                        { pageSize: yPagination.pageSize, ...restTableY },
                    );
                },
            ),
        [table.initialState, tableState],
    );

    const reset = useCallback(() => {
        table.setSorting(table.initialState.sorting);
        table.setColumnVisibility(table.initialState.columnVisibility);
        table.setColumnPinning(table.initialState.columnPinning);
        table.setColumnOrder(table.initialState.columnOrder);
        table.setPagination({
            pageIndex: tableState.pagination.pageIndex,
            pageSize: table.initialState.pagination.pageSize,
        });
    }, [table, tableState.pagination.pageIndex]);

    return { canReset, reset };
};

export { TableReset, useTableReset };
export type { TableResetProps };
